import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, EyeOff, Trophy } from 'lucide-react';
import clsx from 'clsx';
import type { LeaderboardResponse, LeaderboardRow } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { formatINR } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { MonthPicker } from '../components/teacher-reports/MonthPicker';
import { academyMonth, formatMonth, shiftMonth } from '../components/teacher-reports/format';
import { isReportsDisabled } from '../components/teacher-reports/useDueReports';

export const Route = createFileRoute('/_authed/academics/report-leaderboard')({
  component: LeaderboardPage,
});

const card = 'rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]';

function LeaderboardPage() {
  const canView = useAuthStore((s) => s.hasRole('super_admin', 'academic_admin', 'nalim'));
  if (!canView) {
    return (
      <ListShell title="Leaderboard">
        <EmptyState
          title="Management only"
          description="Leaderboards are only visible to Nalims and admins. Your own points are always on My points."
          icon={<EyeOff size={32} aria-hidden="true" />}
        />
      </ListShell>
    );
  }
  return <Leaderboard />;
}

function Leaderboard() {
  const [month, setMonth] = useState(academyMonth);
  const query = useQuery({
    queryKey: ['teacher-reports', 'leaderboard', month] as const,
    queryFn: ({ signal }) => apiRequest<LeaderboardResponse>('/api/teacher-reports/leaderboard', { signal, query: { month } }),
  });
  const rows = query.data?.rows ?? [];
  const previous = formatMonth(shiftMonth(month, -1)).split(' ')[0]!.slice(0, 3);
  const isCurrent = month === academyMonth();

  return (
    <ListShell
      title="Leaderboard"
      description={`Ranked by banked points${isCurrent ? ', month to date' : ''}. Use it to spot who needs support — not only who to reward.`}
      primaryAction={<MonthPicker month={month} onChange={setMonth} />}
    >
      <div className="space-y-4">
      <p className="flex items-center gap-2 rounded-lg border border-dashed border-[color:var(--color-border-strong)] px-3 py-2 text-xs text-[color:var(--color-text-muted)]">
        <EyeOff size={14} aria-hidden="true" /> Management only — teachers and students never see this page or anyone else’s points.
      </p>

      {query.isLoading ? (
        <div className="grid gap-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : query.isError ? (
        isReportsDisabled(query.error) ? (
          <EmptyState title="Teacher reports are switched off" icon={<Trophy size={32} aria-hidden="true" />} />
        ) : (
          <ErrorState
            title="Could not load the leaderboard"
            description={query.error instanceof ApiError ? query.error.message : undefined}
            onRetry={() => void query.refetch()}
          />
        )
      ) : rows.length === 0 ? (
        <EmptyState title="No teachers yet" description="Teachers with active batches appear here." icon={<Trophy size={32} aria-hidden="true" />} />
      ) : (
        <>
          {rows.length >= 3 && rows[0]!.banked > 0 ? <Podium rows={rows} /> : null}

          <section className={clsx(card, 'overflow-hidden')}>
            <table className="hidden w-full text-sm md:table">
              <thead className="bg-[color:var(--color-surface-strong)] text-left text-[11px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
                <tr>
                  <th className="px-4 py-2 font-semibold">Teacher</th>
                  <th className="px-3 py-2 font-semibold">Batches</th>
                  <th className="px-3 py-2 text-right font-semibold">On time</th>
                  <th className="px-3 py-2 text-right font-semibold">Reports</th>
                  <th className="px-3 py-2 text-right font-semibold">Banked</th>
                  <th className="px-3 py-2 text-right font-semibold">Pending</th>
                  <th className="px-3 py-2 text-right font-semibold">Expected ₹</th>
                  <th className="px-4 py-2 text-right font-semibold">vs {previous}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--color-border)]">
                {rows.map((r, i) => (
                  <tr key={r.teacherId}>
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-2 font-semibold">
                        <Rank n={i + 1} />
                        {r.teacherName}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-[color:var(--color-text-muted)]">{r.batchNames.join(', ')}</td>
                    <td className={clsx('px-3 py-2.5 text-right tabular-nums', lowOnTime(r) && 'text-[color:var(--color-danger)]')}>{pct(r)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {r.submitted}
                      {r.missed ? <span className="text-[color:var(--color-danger)]"> · {r.missed} missed</span> : null}
                    </td>
                    <td className="px-3 py-2.5 text-right font-bold tabular-nums">{r.banked}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-[color:var(--color-text-muted)]">{r.pending || '–'}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatINR(r.incentivePaise)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Trend r={r} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <ul className="divide-y divide-[color:var(--color-border)] md:hidden">
              {rows.map((r, i) => (
                <li key={r.teacherId} className="flex items-start gap-3 px-3 py-3">
                  <Rank n={i + 1} />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">{r.teacherName}</p>
                    <p className="truncate text-xs text-[color:var(--color-text-muted)]">{r.batchNames.join(', ')}</p>
                    <p className="mt-1 flex flex-wrap gap-x-3 text-xs tabular-nums">
                      <span className={clsx(lowOnTime(r) && 'text-[color:var(--color-danger)]')}>{pct(r)} on time</span>
                      <span>
                        {r.submitted} report{r.submitted === 1 ? '' : 's'}{r.missed ? ` · ${r.missed} missed` : ''}
                      </span>
                      <span>{formatINR(r.incentivePaise)}</span>
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-bold tabular-nums">{r.banked} points</p>
                    <Trend r={r} />
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {query.data!.needsAttention.length > 0 ? (
            <section className={clsx(card, 'px-4 py-3')}>
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <AlertTriangle size={16} aria-hidden="true" /> Needs attention
              </h2>
              <ul className="mt-1 divide-y divide-dashed divide-[color:var(--color-border)] text-sm">
                {query.data!.needsAttention.map((n, i) => (
                  <li key={`${n.teacherId}-${i}`} className="py-1.5">
                    <b>{n.teacherName}</b> — {n.reason}.
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
      </div>
    </ListShell>
  );
}

const pct = (r: LeaderboardRow) => (r.onTimePct === null ? '–' : `${r.onTimePct}%`);
const lowOnTime = (r: LeaderboardRow) => r.onTimePct !== null && r.onTimePct < 70;

function Rank({ n }: { n: number }) {
  return (
    <span
      className={clsx(
        'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums',
        n <= 3 ? 'bg-gold-500 text-ink-900' : 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text-muted)]',
      )}
    >
      {n}
    </span>
  );
}

function Trend({ r }: { r: LeaderboardRow }) {
  const diff = r.banked - r.previousBanked;
  if (!r.previousBanked) return <span className="text-xs text-[color:var(--color-text-muted)]">–</span>;
  return (
    <span className={clsx('text-xs font-semibold tabular-nums', diff > 0 ? 'text-[color:var(--color-success)]' : diff < 0 ? 'text-[color:var(--color-danger)]' : 'text-[color:var(--color-text-muted)]')}>
      {diff > 0 ? '▲' : diff < 0 ? '▼' : '='} {Math.abs(diff)}
      <span className="sr-only"> points compared with last month</span>
    </span>
  );
}

function Podium({ rows }: { rows: LeaderboardRow[] }) {
  const [first, second, third] = rows;
  const step = (r: LeaderboardRow, n: number, height: string) => (
    <div className="flex min-w-0 flex-1 flex-col items-center">
      <span className="mb-1 max-w-full truncate text-sm font-semibold">{r.teacherName}</span>
      <span className="mb-2 text-xs tabular-nums text-[color:var(--color-text-muted)]">
        {r.banked} points{r.onTimePct !== null ? ` · ${r.onTimePct}%` : ''}
      </span>
      <div
        className={clsx(
          'flex w-full items-start justify-center rounded-t-lg pt-2 font-display text-2xl font-extrabold',
          n === 1 ? 'bg-gold-500 text-ink-900' : 'bg-gold-500/25 text-gold-800 dark:text-gold-200',
          height,
        )}
      >
        {n}
      </div>
    </div>
  );
  return (
    <section className={clsx(card, 'px-4 pt-4')} aria-label="Top three">
      <div className="mx-auto flex max-w-lg items-end gap-2 sm:gap-4">
        {step(second!, 2, 'h-16')}
        {step(first!, 1, 'h-24')}
        {step(third!, 3, 'h-12')}
      </div>
    </section>
  );
}
